import { AssetTypeCode, ComplexityLevel } from '@atlas/types'
import { SectionCard, SelectField } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { FieldWrap } from '@/pages/Classification/FormField'
import { ErrorBanner, FieldError } from './states'

/**
 * The resolver-rule editor's fields, prop-driven so it renders in isolation.
 * Create and edit use the same form: matchers, target workflow, and the same
 * refusal of a rule that would match nothing. The target workflow is a matcher
 * outcome rather than an ownership relationship, so it is editable on an
 * existing rule exactly as it is on a new one.
 */

const ASSET_TYPES = Object.values(AssetTypeCode)
const COMPLEXITIES = Object.values(ComplexityLevel)

export interface ResolverRuleFormProps {
	/** True when editing a persisted rule rather than creating one. */
	isEdit: boolean
	assetTypes: string[]
	complexities: string[]
	workflowId: string
	workflowOptions: readonly { value: string; label: string }[]
	onAssetTypesChange: (values: string[]) => void
	onComplexitiesChange: (values: string[]) => void
	onWorkflowIdChange: (workflowId: string) => void
	onSubmit: () => void
	onCancel: () => void
	isPending?: boolean
	/** A server message that belongs against the target-workflow field. */
	targetError?: string
	/** A server error not attributable to a single field. */
	saveError?: unknown
}

function MultiSelect({
	label,
	values,
	options,
	onChange,
}: {
	label: string
	values: string[]
	options: readonly string[]
	onChange: (values: string[]) => void
}) {
	return (
		<FieldWrap label={label}>
			<select
				aria-label={label}
				multiple
				className="border rounded-ds-base px-2 py-1 h-24"
				value={values}
				onChange={(e) => onChange(Array.from(e.target.selectedOptions, (option) => option.value))}
			>
				{options.map((option) => (
					<option key={option} value={option}>
						{option}
					</option>
				))}
			</select>
		</FieldWrap>
	)
}

export function ResolverRuleForm(props: ResolverRuleFormProps) {
	const { assetTypes, complexities, workflowId, isPending, targetError, saveError } = props
	// The backend is authoritative, but a rule with both matchers absent matches
	// everything and is refused there, so it is refused here rather than sent.
	const bothNull = assetTypes.length === 0 && complexities.length === 0
	const invalid = bothNull || workflowId === '' || !!isPending

	return (
		<SectionCard
			title={props.isEdit ? 'Edit resolver rule' : 'New resolver rule'}
			description={'Leave a matcher unselected to mean "any". At least one matcher is required.'}
		>
			<div className="flex flex-wrap items-end gap-3">
				<MultiSelect
					label="Asset types"
					values={assetTypes}
					options={ASSET_TYPES}
					onChange={props.onAssetTypesChange}
				/>
				<MultiSelect
					label="Complexity"
					values={complexities}
					options={COMPLEXITIES}
					onChange={props.onComplexitiesChange}
				/>
				<div className="flex flex-col">
					<SelectField
						isClearable={false}
						label="Target workflow"
						value={workflowId}
						onChange={props.onWorkflowIdChange}
						isDisabled={isPending}
						options={[{ value: '', label: 'Select…' }, ...props.workflowOptions]}
					/>
					<FieldError message={targetError} />
				</div>
				<Button isDisabled={invalid} onPress={props.onSubmit}>
					{isPending ? 'Saving…' : 'Save'}
				</Button>
				<Button variant="outline" onPress={props.onCancel}>
					Cancel
				</Button>
				{bothNull && <FieldError message="Select at least one asset type or complexity level." />}
				{saveError && !targetError ? <ErrorBanner error={saveError} title="Could not save rule" /> : null}
			</div>
		</SectionCard>
	)
}
